import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import MoreVert from "@mui/icons-material/MoreVert";
import { Box, type BoxProps, Button, Typography } from "@mui/material";
import { ErrorBoundary } from "@sentry/react";
import type React from "react";
import type { FC, ReactElement } from "react";
import type { OpenState } from "../../../moment/component/config";

type ValidState = {
  lastFormAction?: "publish" | "draft";
  valid: boolean;
};

export interface MomentAccordionProps {
  id: string;
  title: string;
  subtitle: string;
  index: number;
  open: OpenState;
  valid: ValidState;
  summaryIcon?: ReactElement | null;
  setOpen?: (open: OpenState) => void;
  onConfirm?: () => void | Promise<void>;
  disableToggle?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  sx?: BoxProps["sx"];
}

export const MomentAccordion: FC<MomentAccordionProps> = ({
  open,
  title,
  subtitle,
  index,
  children,
  valid,
  summaryIcon,
  setOpen,
  onConfirm,
  id,
  disabled,
  sx,
}): ReactElement => {
  const handleToggle = (): void => setOpen && setOpen(open === "open" ? "closed" : "open");

  const highlightErrors = valid.lastFormAction === "publish" && !valid.valid;

  const isOpen = open === "open";

  return (
    <ErrorBoundary
      fallback={
        <Box>
          There was an error rendering some content on this page. If this error persists, please
          contact customer support.
        </Box>
      }
    >
      <Box
        id={id}
        data-testid={id}
        data-highlight-errors={highlightErrors}
        sx={{
          borderRadius: 1,
          boxShadow: 1,
          ...(highlightErrors
            ? {
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: "error.main",
              }
            : !isOpen
              ? {
                  borderTop: "1px solid",
                  borderTopColor: "divider",
                }
              : {}),
          ...sx,
        }}
      >
        {/* Summary - always visible, acts as the toggle */}
        <Box
          role="button"
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleToggle();
            }
          }}
          data-analytics-id={`moment-accordion-${id}-toggle`}
          aria-expanded={isOpen}
          sx={{ cursor: "pointer", px: 2, py: 1.5 }}
        >
          <Box
            sx={{
              minHeight: "100%",
              minWidth: "100%",
              alignItems: "center",
              display: "flex",
              ml: -2,
              my: -1.5,
            }}
          >
            <NumberBox number={index} open={open} valid={valid} highlightErrors={highlightErrors} />
            <Title text={title} valid={valid} open={isOpen} />
            <Subtitle text={subtitle} valid={valid} open={isOpen} />

            <Box sx={{ ml: "auto", mt: 1 }}>
              {summaryIcon}
              <MoreVert sx={{ width: "40px", height: "50px", color: "text.disabled" }} />
            </Box>
          </Box>
        </Box>

        {/* Details - shown on open */}
        <Box
          sx={{
            overflow: "hidden",
            maxHeight: isOpen ? "200vh" : 0,
            visibility: isOpen ? "visible" : "hidden",
            transition: isOpen
              ? "max-height 500ms ease-in, visibility 0ms"
              : "max-height 150ms ease-out, visibility 0ms 150ms",
          }}
        >
          <Box sx={{ pb: 3, pl: 8, pr: 8 }}>
            {children}
            {onConfirm && !disabled && (
              <Button
                data-analytics-id={`moment-accordion-${id}-confirm`}
                sx={{ mt: 3 }}
                variant="outlined"
                color="secondary"
                onClick={() => void onConfirm()?.catch(console.error)}
              >
                Continue
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

const Title: FC<{ text: string; valid: ValidState; open: boolean }> = ({ text, valid, open }) => {
  return (
    <Box sx={{ ml: 4, width: 120 }}>
      {!open && (
        <Typography variant="h6" color={valid.valid ? "text.disabled" : undefined}>
          {text}
        </Typography>
      )}
    </Box>
  );
};

const Subtitle: FC<{ text: string; valid: ValidState; open: boolean }> = ({
  text,
  valid,
  open,
}) => {
  if (valid.valid || open) return null;

  return (
    <Box>
      <Typography variant="subtitle2" color="text.disabled">
        {text}
      </Typography>
    </Box>
  );
};

const NumberBox: FC<{
  number: number;
  valid: ValidState;
  open: OpenState;
  highlightErrors: boolean;
}> = ({ number, valid, open, highlightErrors }) => {
  if (valid.valid && (Boolean(valid.lastFormAction) || open === "confirmed")) {
    return (
      <Box
        sx={{
          backgroundColor: "secondary.main",
          pl: "16px",
          pr: "14px",
          pt: "14px",
          pb: "12px",
        }}
      >
        <CheckCircleOutline htmlColor="white" fontSize="large" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: highlightErrors ? "error.main" : "background.contrast",
        px: 3,
        py: 2,
        width: 64,
      }}
    >
      <Typography variant="h5" color={highlightErrors ? "error.contrastText" : undefined}>
        {number}
      </Typography>
    </Box>
  );
};
